export type AnnualReviewStatus =
  | 'PENDING_BOTH'
  | 'PENDING_EMPLOYEE'
  | 'PENDING_SUPERVISOR'
  | 'AWAITING_GROWTH_PLAN'
  | 'AWAITING_PAY_RAISE_DECISION'
  | 'AWAITING_SIGNATURES'
  | 'FINISHED'
  | 'CANCELED';

export interface AnnualReviewPayRateDecision {
  outcome: 'approved' | 'denied';
  finalPayRate?: number | null;
  paymentFrequency?: string | null;
  effectiveAt?: string | null;
  deniedReason?: string | null;
  decidedAt?: string | null;
}

export interface AnnualReviewEmployeeAnswers {
  ratings?: Record<string, number>;
  examples?: Record<string, string>;
  selfReflection?: Record<string, string>;
}

export interface AnnualReviewSupervisorAnswers {
  ratings?: Record<string, number>;
  examples?: Record<string, string>;
  directorReview?: {
    topStrengths?: string[];
    opportunitiesForGrowth?: string[];
  };
}

export interface AnnualReviewGrowthPlan {
  professionalGoal?: string;
  personalGoal?: string;
  actionSteps?: string[];
  leadershipSupport?: string;
}

export interface AnnualReviewProposedPayRate {
  currentPayRate?: number | null;
  increasePercent?: number | null;
  newPayRate?: number | null;
  paymentFrequency?: string | null;
  decision?: AnnualReviewPayRateDecision | null;
}

export interface AnnualReviewResponse {
  id: string;
  userId: string;
  schoolId: string;
  supervisorId: string | null;
  cycleYear: number;
  employeeToken: string | null;
  status: AnnualReviewStatus;
  employeeName: string;
  employeeId: string | null;
  employeeStartDate: string | null;
  supervisorName: string;
  employeeAnswers: AnnualReviewEmployeeAnswers | null;
  employeeCompletedAt: string | null;
  supervisorAnswers: AnnualReviewSupervisorAnswers | null;
  supervisorCompletedAt: string | null;
  growthPlan: AnnualReviewGrowthPlan | null;
  growthPlanFilledAt: string | null;
  proposedPayRate: AnnualReviewProposedPayRate | null;
  createdPayRateId: string | null;
  draftCombinedPdfUrl: string | null;
  finalSignedPdfUrl: string | null;
  finalSignedAt: string | null;
  averageRatings: Record<string, number | null>;
  coreValuesAverage: number | null;
  createdAt: string;
  updatedAt: string;
}

export type StepKey =
  | 'section_1'
  | 'section_2'
  | 'section_3'
  | 'section_4'
  | 'self_reflection'
  | 'director_review'
  | 'review_submit';

export interface AnnualReviewStepDescriptor {
  key: StepKey;
  title: string;
  forEmployee: boolean;
  forSupervisor: boolean;
}

export const EMPLOYEE_STEPS: AnnualReviewStepDescriptor[] = [
  { key: 'section_1', title: 'The AEA Way (Core Performance)', forEmployee: true, forSupervisor: false },
  { key: 'section_2', title: 'Team & Culture', forEmployee: true, forSupervisor: false },
  { key: 'section_3', title: 'Growth & Mindset', forEmployee: true, forSupervisor: false },
  { key: 'section_4', title: 'AEA Differentiators', forEmployee: true, forSupervisor: false },
  { key: 'self_reflection', title: 'Self-Reflection', forEmployee: true, forSupervisor: false },
  { key: 'review_submit', title: 'Review & Submit', forEmployee: true, forSupervisor: false },
];

export const SUPERVISOR_STEPS: AnnualReviewStepDescriptor[] = [
  { key: 'section_1', title: 'The AEA Way (Core Performance)', forEmployee: false, forSupervisor: true },
  { key: 'section_2', title: 'Team & Culture', forEmployee: false, forSupervisor: true },
  { key: 'section_3', title: 'Growth & Mindset', forEmployee: false, forSupervisor: true },
  { key: 'section_4', title: 'AEA Differentiators', forEmployee: false, forSupervisor: true },
  { key: 'director_review', title: 'Director Review', forEmployee: false, forSupervisor: true },
  { key: 'review_submit', title: 'Review & Submit', forEmployee: false, forSupervisor: true },
];

export const resumeStepIndex = (steps: AnnualReviewStepDescriptor[], completedKeys: Set<string>): number => {
  const idx = steps.findIndex((s) => s.key !== 'review_submit' && !completedKeys.has(s.key));
  return idx === -1 ? steps.length - 1 : idx;
};
